import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, AnimatePresence, useScroll, useTransform } from 'motion/react';
import {
  FiZap, FiLayout, FiCpu, FiClock, FiShield, FiArrowRight,
  FiTrendingUp, FiEdit, FiSend, FiImage, FiTarget, FiRefreshCw,
} from 'react-icons/fi';
import usePageMeta from '../shared/hooks/usePageMeta';
import { SUPPORT_EMAIL } from '../shared/constants/contactInfo';

const REDUCED_MOTION = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ------------------------------------------------------------------ */
/*  Rotating hero words                                                */
/* ------------------------------------------------------------------ */
const HERO_WORDS = ['Copywriter', 'Designer', 'Videographer', 'Strategist', 'Content Team'];
const HERO_TIMINGS = [2500, 2500, 2500, 2500, 4500]; // last word stays longer

function RotatingWord() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (REDUCED_MOTION) return;
    let timeout;
    const advance = () => {
      setIndex(prev => {
        const next = (prev + 1) % HERO_WORDS.length;
        timeout = setTimeout(advance, HERO_TIMINGS[next]);
        return next;
      });
    };
    timeout = setTimeout(advance, HERO_TIMINGS[0]);
    return () => clearTimeout(timeout);
  }, []);

  if (REDUCED_MOTION) return <span className="font-serif italic text-slate-blue">Content Team</span>;

  const isLast = index === HERO_WORDS.length - 1;

  return (
    <AnimatePresence mode="wait">
      <motion.span
        key={HERO_WORDS[index]}
        initial={{ y: 40, opacity: 0, filter: 'blur(8px)' }}
        animate={isLast
          ? { y: 0, opacity: 1, filter: 'blur(0px)', rotate: [0, -2, 2, -1.5, 1.5, -0.5, 0] }
          : { y: 0, opacity: 1, filter: 'blur(0px)' }
        }
        exit={{ y: -40, opacity: 0, filter: 'blur(8px)' }}
        transition={isLast
          ? { duration: 0.35, ease: [0.22, 1, 0.36, 1], rotate: { delay: 0.4, duration: 0.5, ease: 'easeInOut' } }
          : { duration: 0.35, ease: [0.22, 1, 0.36, 1] }
        }
        className="inline-block font-serif italic text-slate-blue"
      >
        {HERO_WORDS[index]}
      </motion.span>
    </AnimatePresence>
  );
}

/* ------------------------------------------------------------------ */
/*  Hero showcase — vertical film strip columns with all vibe previews */
/* ------------------------------------------------------------------ */
const VIBE_IDS_IMAGE = [
  '3d_geometric','3d_rounded_toy','amateur_mobile','anime','claymation','clean_bright',
  'close_up','comic_book','cyberpunk_neon','dark_fantasy','dreamlike','faded_polaroid',
  'fantasy','fashion_editorial','film_noir_classic','flat_illustration','ghibli_anime',
  'golden_hour','hand_drawn','illustrated_storybook','ink_style','kodachrome','minimalist',
  'moody_drama','nature_documentary','pixel_art','portrait','raw_documentary',
  'selfie_influencer','smart_auto','stop_motion','super_8_film','surreal','ultra_real',
  'vhs_retro','viral','warm_editorial','watercolor',
];
const VIBE_IDS_VIDEO = [...VIBE_IDS_IMAGE, 'asmr'];
const SUFFIXES = ['a', 'b', 'c'];

function buildShuffledImages() {
  const all = [];
  VIBE_IDS_IMAGE.forEach(v => SUFFIXES.forEach(s => all.push({ src: `/images/vibes/image/${v}_${s}.webp`, vibe: v })));
  VIBE_IDS_VIDEO.forEach(v => SUFFIXES.forEach(s => all.push({ src: `/images/vibes/video/${v}_${s}.webp`, vibe: v })));

  // Fisher-Yates shuffle
  for (let i = all.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [all[i], all[j]] = [all[j], all[i]];
  }

  // Fix adjacent same-vibe: scan and swap with next different
  for (let i = 1; i < all.length; i++) {
    if (all[i].vibe === all[i - 1].vibe) {
      for (let j = i + 1; j < all.length; j++) {
        if (all[j].vibe !== all[i - 1].vibe && (i + 1 >= all.length || all[j].vibe !== all[i + 1]?.vibe)) {
          [all[i], all[j]] = [all[j], all[i]];
          break;
        }
      }
    }
  }
  return all;
}

const HERO_POOL = buildShuffledImages();
const COL_COUNT = 5;
const HERO_COLUMNS = Array.from({ length: COL_COUNT }, (_, c) =>
  HERO_POOL.filter((_, i) => i % COL_COUNT === c).map(img => img.src)
);

// Scroll speeds (seconds for one full cycle) — slow and dreamy, varied per column
const COL_SPEEDS = [220, 280, 240, 300, 260];
const COL_DIRECTIONS = ['up', 'down', 'up', 'down', 'up'];

function FilmStripColumn({ images, speed, direction }) {
  const doubled = [...images, ...images];
  const animName = direction === 'up' ? 'filmstrip-up' : 'filmstrip-down';

  return (
    <div className="w-[180px] flex-shrink-0 overflow-hidden relative">
      <div
        style={REDUCED_MOTION ? {} : { animation: `${animName} ${speed}s linear infinite` }}
      >
        {doubled.map((src, i) => (
          <div key={i} className="p-1.5">
            <div className="rounded-2xl overflow-hidden">
              <img
                src={src}
                alt=""
                loading="lazy"
                className="w-full aspect-[9/16] object-cover"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function HeroFilmStrips() {
  return (
    <>
      {/* Film strips — full-bleed behind entire hero, fixed column widths */}
      <div className="absolute inset-0 overflow-hidden opacity-50" style={{ perspective: '1400px' }}>
        <div
          className="flex gap-2 justify-center h-full"
          style={{ transform: 'rotateY(-4deg) rotateX(2deg)', transformOrigin: '60% 50%', transformStyle: 'preserve-3d' }}
        >
          {HERO_COLUMNS.map((col, i) => (
            <FilmStripColumn key={i} images={col} speed={COL_SPEEDS[i]} direction={COL_DIRECTIONS[i]} />
          ))}
        </div>
      </div>

      {/* Gradient overlays — softer left, fade all edges */}
      <div className="absolute inset-0 pointer-events-none z-[2]">
        {/* Left — softer overlay so left column is faintly visible */}
        <div className="absolute inset-y-0 left-0 w-[45%] bg-gradient-to-r from-warm-dark/90 via-warm-dark/80 to-transparent" />
        {/* Top fade */}
        <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-warm-dark to-transparent" />
        {/* Bottom fade */}
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-warm-dark to-transparent" />
        {/* Right edge fade */}
        <div className="absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-warm-dark/80 to-transparent" />
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Feature marquee — replaces stats strip                             */
/* ------------------------------------------------------------------ */
const MARQUEE_FEATURES = [
  { icon: FiCpu, label: 'AI Content Generation' },
  { icon: FiImage, label: 'Visual Studio' },
  { icon: FiClock, label: 'Smart Scheduling' },
  { icon: FiTrendingUp, label: 'Trend Intelligence' },
  { icon: FiZap, label: 'Instant Publishing' },
  { icon: FiRefreshCw, label: 'Content Streams' },
  { icon: FiTarget, label: 'Brand Profiling' },
  { icon: FiShield, label: 'Enterprise Security' },
];

function FeatureMarquee() {
  if (REDUCED_MOTION) {
    return (
      <div className="flex flex-wrap justify-center gap-6 py-6 px-4">
        {MARQUEE_FEATURES.map((item, i) => (
          <div key={i} className="flex items-center gap-2">
            <item.icon className="text-slate-blue" />
            <span className="text-cream/50 text-sm font-medium">{item.label}</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden group">
      {/* Fade edges */}
      <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-warm-dark to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-warm-dark to-transparent z-10 pointer-events-none" />

      <div className="flex animate-marquee hover:[animation-play-state:paused]">
        {[...MARQUEE_FEATURES, ...MARQUEE_FEATURES].map((item, i) => (
          <div key={i} className="flex-shrink-0 flex items-center gap-3 px-8 py-6">
            <item.icon className="text-slate-blue text-lg flex-shrink-0" />
            <span className="text-cream/50 text-sm font-medium whitespace-nowrap">{item.label}</span>
            <span className="text-cream/10 ml-2">&#9670;</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Scroll-triggered section wrapper                                   */
/* ------------------------------------------------------------------ */
function AnimatedSection({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.15 });
  if (REDUCED_MOTION) return <div ref={ref} className={className}>{children}</div>;
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.7, delay }}
    >
      {children}
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*  Data                                                                */
/* ------------------------------------------------------------------ */
const FEATURES = [
  { icon: <FiCpu />, title: 'AI Content Core', description: 'Generate endless streams of on-brand posts, captions, and visual concepts tailored to your specific audience.', color: 'from-slate-blue/20 to-slate-blue/5' },
  { icon: <FiLayout />, title: 'Visual Studio', description: 'Create stunning visuals with our integrated generative art engine. Perfect aspect ratios for every platform.', color: 'from-emerald-500/20 to-emerald-500/5' },
  { icon: <FiClock />, title: 'Smart Scheduling', description: 'Plan your entire month in minutes. Our intelligent calendar ensures your content hits at the perfect time.', color: 'from-matcha/20 to-matcha/5' },
  { icon: <FiZap />, title: 'Instant Deploy', description: 'Push content directly to Meta platforms with a single click. No more downloading and re-uploading.', color: 'from-clay/20 to-clay/5' },
  { icon: <FiTrendingUp />, title: 'Trend Intelligence', description: "AI-powered niche trend discovery finds what's relevant to your brand right now, with ready-made content angles.", color: 'from-pale-lilac/20 to-pale-lilac/5' },
  { icon: <FiShield />, title: 'Secure & Private', description: 'Your data and brand voice are protected. Enterprise-grade security for creators of all sizes.', color: 'from-cream/10 to-cream/5' },
];

const HOW_IT_WORKS_STEPS = [
  { step: '01', title: 'Describe Your Brand', description: 'Tell us about your business \u2014 industry, voice, and audience. Our AI builds your brand profile.', icon: FiEdit, iconColor: 'bg-slate-blue/10 text-slate-blue border-slate-blue/20' },
  { step: '02', title: 'Generate Content', description: 'AI creates on-brand posts with copy, visuals, and scheduling \u2014 ready to publish in minutes.', icon: FiCpu, iconColor: 'bg-matcha/10 text-matcha border-matcha/20' },
  { step: '03', title: 'Publish & Grow', description: 'Deploy directly to Facebook and Instagram. Track what works and scale your content strategy.', icon: FiSend, iconColor: 'bg-clay/10 text-clay border-clay/20' },
];

/* ------------------------------------------------------------------ */
/*  Main LandingPage                                                   */
/* ------------------------------------------------------------------ */
function LandingPage() {
  usePageMeta(
    'AI-Powered Social Media Content Automation',
    'NextAInsight combines advanced AI with organic workflows to generate, schedule, and publish high-impact social media content for Facebook and Instagram.'
  );

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroOpacity = useTransform(scrollYProgress, [0, 1], [1, 0]);
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 100]);

  return (
    <>
      {/* ===== HERO ===== */}
      <section ref={heroRef} className="relative pt-20 pb-32 px-4 sm:px-6 overflow-hidden min-h-[90vh] flex items-center">
        {/* Full-bleed film strip background */}
        <HeroFilmStrips />

        {/* Dot grid overlay */}
        <div
          className="absolute inset-0 opacity-[0.04] z-[3]"
          style={{
            backgroundImage: 'radial-gradient(circle, #EBE2DD 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        />

        <motion.div
          style={REDUCED_MOTION ? {} : { opacity: heroOpacity, y: heroY }}
          className="max-w-6xl mx-auto relative z-10 w-full"
        >
          {/* Frosted glass card */}
          <motion.div
            initial={REDUCED_MOTION ? {} : { opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="relative max-w-2xl lg:text-left text-center"
          >
            <div className="relative bg-warm-dark/60 border border-white/[0.08] rounded-[2.5rem] px-8 py-10 sm:px-12 sm:py-14 shadow-[0_8px_60px_rgba(0,0,0,0.4)]">
              <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-8">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-matcha opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-matcha" />
                </span>
                <span className="text-xs font-bold uppercase tracking-widest text-cream/60">Taking on new clients</span>
              </div>

              <h1 className="text-5xl md:text-7xl font-black tracking-tighter leading-[0.95] mb-8">
                <span className="text-transparent bg-clip-text bg-gradient-to-b from-cream via-cream to-cream/60">One Tool.</span>
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-b from-cream via-cream to-cream/40">Your Entire</span>
                <br />
                <RotatingWord />
              </h1>

              <p className="text-lg text-cream/60 mb-10 leading-relaxed max-w-lg mx-auto lg:mx-0">
                Describe your brand. Get AI&#8209;written copy, generated images and videos in any style, automated scheduling, and one&#8209;click publishing.{' '}
                <motion.span
                  initial={REDUCED_MOTION ? {} : { opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.2, duration: 0.6 }}
                  className="text-cream font-semibold"
                >
                  Done.
                </motion.span>
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="group relative px-8 py-4 bg-cream text-warm-dark text-sm font-black uppercase tracking-widest rounded-full hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2 shadow-[0_0_30px_rgba(235,226,221,0.15)] hover:shadow-[0_0_50px_rgba(235,226,221,0.25)]"
                >
                  Work With Us
                  <FiZap className="group-hover:rotate-12 transition-transform" />
                </a>
                <Link
                  to="/how-it-works"
                  className="px-8 py-4 bg-white/[0.03] text-cream text-sm font-bold uppercase tracking-widest rounded-full hover:bg-white/[0.08] backdrop-blur-md border border-white/10 transition-all flex items-center justify-center gap-2 group"
                >
                  See The Magic
                  <FiArrowRight className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* ===== FEATURE MARQUEE ===== */}
      <section className="border-y border-white/5 bg-warm-black/50 backdrop-blur-sm relative z-10">
        <FeatureMarquee />
      </section>

      {/* ===== FEATURES GRID ===== */}
      <section className="py-28 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <AnimatedSection>
            <div className="text-center mb-20">
              <span className="text-xs font-bold uppercase tracking-widest text-slate-blue mb-4 block">Everything You Need</span>
              <h2 className="text-4xl md:text-5xl font-black tracking-tight mb-5 text-cream">
                Engineered for{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-slate-blue to-matcha">Growth</span>
              </h2>
              <p className="text-cream/40 max-w-xl mx-auto text-lg">One organic workspace. Infinite creative possibilities.</p>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((feature, i) => (
              <motion.div
                key={feature.title}
                initial={REDUCED_MOTION ? {} : { opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                whileHover={REDUCED_MOTION ? {} : { y: -6, transition: { duration: 0.2 } }}
                className="group relative p-8 bg-warm-gray/20 border border-white/5 rounded-2xl hover:border-white/15 transition-all duration-300 overflow-hidden"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                <div className="relative z-10">
                  <div className="w-12 h-12 bg-white/5 rounded-xl flex items-center justify-center text-slate-blue text-xl mb-5 group-hover:scale-110 group-hover:bg-white/10 transition-all duration-300">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-cream group-hover:text-white transition-colors">{feature.title}</h3>
                  <p className="text-cream/45 leading-relaxed text-sm">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section className="py-28 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-warm-gray/10 to-transparent" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
          <AnimatedSection>
            <div className="text-center mb-16">
              <span className="text-xs font-bold uppercase tracking-widest text-slate-blue mb-4 block">Simple Process</span>
              <h2 className="text-4xl md:text-5xl font-black tracking-tight mb-5 text-cream">How It Works</h2>
              <p className="text-cream/40 max-w-xl mx-auto text-lg">From brand setup to published content in three steps.</p>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12 relative">
            {/* Connecting line (desktop) */}
            <div className="hidden md:block absolute top-1/2 left-[16%] right-[16%] h-px bg-gradient-to-r from-slate-blue/20 via-matcha/30 to-clay/20 -translate-y-1/2 z-0" />

            {HOW_IT_WORKS_STEPS.map((item, i) => {
              const IconComp = item.icon;
              return (
                <motion.div
                  key={item.step}
                  initial={REDUCED_MOTION ? {} : { opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.5, delay: i * 0.15 }}
                  className="relative p-8 bg-warm-gray/20 backdrop-blur-md border border-white/5 rounded-2xl text-center z-10"
                >
                  <div className={`w-14 h-14 mx-auto mb-4 rounded-xl flex items-center justify-center text-xl border ${item.iconColor}`}>
                    <IconComp />
                  </div>
                  <span className="inline-block px-3 py-1 rounded-full bg-slate-blue/10 text-slate-blue text-xs font-bold mb-4">Step {item.step}</span>
                  <h3 className="text-xl font-bold text-cream mb-3">{item.title}</h3>
                  <p className="text-cream/50 leading-relaxed text-sm">{item.description}</p>
                </motion.div>
              );
            })}
          </div>

          <AnimatedSection className="text-center">
            <Link
              to="/how-it-works"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/5 border border-white/10 text-slate-blue font-bold hover:bg-white/10 transition-colors group"
            >
              See the full 8-step workflow
              <FiArrowRight className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </AnimatedSection>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section className="py-28 px-4">
        <AnimatedSection>
          <div className="max-w-5xl mx-auto text-center relative">
            <div
              className="absolute inset-0 -inset-x-20 opacity-20 blur-[100px] rounded-full pointer-events-none"
              style={{ background: 'radial-gradient(ellipse at center, #7B8FA1 0%, transparent 70%)' }}
            />

            <div className="relative z-10 py-16">
              <motion.h2
                initial={REDUCED_MOTION ? {} : { opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="text-4xl md:text-6xl font-black tracking-tighter mb-6 text-cream"
              >
                Your Content Engine{' '}
                <br className="hidden md:block" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-slate-blue to-matcha">Awaits</span>
              </motion.h2>
              <p className="text-cream/50 text-lg mb-10 max-w-2xl mx-auto">
                We run social media content for brands &mdash; strategy, copy, visuals and scheduling, handled end to end.
              </p>

              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="inline-flex items-center gap-3 px-10 py-5 bg-cream text-warm-dark text-sm font-black uppercase tracking-widest rounded-full hover:scale-105 transition-all duration-300 shadow-[0_0_40px_rgba(235,226,221,0.15)] hover:shadow-[0_0_60px_rgba(235,226,221,0.25)] group"
              >
                Get In Touch
                <FiArrowRight className="group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
        </AnimatedSection>
      </section>
    </>
  );
}

export default LandingPage;
